//src/components/UserProfile.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth, updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { db } from '../firebase';
import Header from './Header';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeApp } from 'firebase/app';

// Màu cho avatar tự động tạo
const AVATAR_COLORS = [
  { bg: 'bg-emerald-200', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-700', darkText: 'dark:text-emerald-100' },
  { bg: 'bg-blue-200', text: 'text-blue-700', darkBg: 'dark:bg-blue-700', darkText: 'dark:text-blue-100' },
  { bg: 'bg-amber-200', text: 'text-amber-700', darkBg: 'dark:bg-amber-700', darkText: 'dark:text-amber-100' },
  { bg: 'bg-pink-200', text: 'text-pink-700', darkBg: 'dark:bg-pink-700', darkText: 'dark:text-pink-100' },
  { bg: 'bg-purple-200', text: 'text-purple-700', darkBg: 'dark:bg-purple-700', darkText: 'dark:text-purple-100' },
  { bg: 'bg-indigo-200', text: 'text-indigo-700', darkBg: 'dark:bg-indigo-700', darkText: 'dark:text-indigo-100' },
  { bg: 'bg-rose-200', text: 'text-rose-700', darkBg: 'dark:bg-rose-700', darkText: 'dark:text-rose-100' },
  { bg: 'bg-cyan-200', text: 'text-cyan-700', darkBg: 'dark:bg-cyan-700', darkText: 'dark:text-cyan-100' },
];

// Hàm chuyển URL thành link có thể click
const linkifyText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const result = [];
  let lastIndex = 0;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }
    let url = match[0];
    const href = url.startsWith('http') ? url : `http://${url}`;
    result.push(
      <a key={`link-${match.index}`} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{url}</a>
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  return result;
};

// Hàm tạo avatar từ tên người dùng
const generateInitialsAvatar = (user, size = 'md') => {
  if (!user) return null;
  
  // Xác định chữ cái đầu tiên từ tên hiển thị hoặc email
  const initial = user?.displayName?.charAt(0) || user?.email?.charAt(0) || '?';
  
  // Sử dụng user.uid để chọn màu nhất quán cho mỗi người dùng
  const colorIndex = user.uid ? 
    [...user.uid].reduce((acc, char) => acc + char.charCodeAt(0), 0) % AVATAR_COLORS.length : 
    0;
  
  const color = AVATAR_COLORS[colorIndex];
  
  // Kích thước dựa trên tham số
  const sizeClasses = size === 'sm' ? 'w-8 h-8' : 
                    size === 'lg' ? 'w-24 h-24 text-4xl' : 
                    'w-10 h-10';
  
  // Hình dạng avatar (tròn hoặc bo góc)
  const shape = 'rounded-full';
  
  return (
    <div className={`${sizeClasses} ${shape} ${color.bg} ${color.text} ${color.darkBg} ${color.darkText} flex items-center justify-center shadow-sm border-4 border-white dark:border-gray-800 font-bold`}>
      {initial.toUpperCase()}
    </div>
  );
};

// Hàm chuyển đổi role sang tiếng Việt
const getRoleLabel = (role) => {
  if (!role) return '';
  switch (role) {
    case 'executor':
    case 'assignee':
    case 'Người thực hiện':
      return 'Người thực hiện';
    case 'collaborator':
    case 'Người phối hợp':
      return 'Người phối hợp';
    case 'supervisor':
    case 'Người giám sát':
      return 'Người giám sát';
    case 'approver':
    case 'Người phê duyệt':
      return 'Người phê duyệt';
    case 'member':
    case 'Thành viên':
      return 'Thành viên';
    default:
      return role;
  }
};

// Helper xác định vai trò của user trong task (cập nhật trả về nhãn tiếng Việt)
const getUserRoleInTask = (task, currentUser) => {
  if (!task || !currentUser) return null;
  if (Array.isArray(task.assignees)) {
    const found = task.assignees.find(a => a.uid === currentUser.uid);
    if (found && found.role) return getRoleLabel(found.role);
    if (found) return 'Người thực hiện';
  }
  if (Array.isArray(task.participants)) {
    const found = task.participants.find(a => a.uid === currentUser.uid);
    if (found && found.role) return getRoleLabel(found.role);
    if (found) return 'Thành viên';
  }
  if (task.approverId === currentUser?.uid) return 'Người phê duyệt';
  return null;
};

const UserProfile = () => {
  const firebaseConfig = {
    apiKey: "AIzaSyBHZTbS1Cap2F-tNzHvU3bWA_4JMPocUFE",
    authDomain: "greenschool-work.firebaseapp.com",
    projectId: "greenschool-work",
    storageBucket: "greenschool-work.firebasestorage.app",
    messagingSenderId: "176601035852",
    appId: "1:176601035852:web:ed82b7ac63f3a74dd9b0e9",
    measurementId: "G-LFSHL3ETP4"
  };
  const { userId } = useParams();
  const navigate = useNavigate();
  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const functions = getFunctions(app);
  const currentUser = auth.currentUser;
  
  const [user, setUser] = useState(null);
  const [userTeams, setUserTeams] = useState([]);
  const [userTasks, setUserTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Trạng thái thông báo
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' // 'success', 'error', 'info'
  });
  
  // Hàm hiển thị thông báo
  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Tự động đóng sau 3 giây
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };
  
  // Trạng thái chế độ chỉnh sửa
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({
    displayName: '',
    email: '',
    bio: '',
    role: '',
    phoneNumber: ''
  });
  
  // Trạng thái thay đổi mật khẩu
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Trạng thái liên quan đến avatar
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // Tham chiếu đến input file
  const fileInputRef = useRef(null);
  
  // Lấy dữ liệu người dùng
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        const user = auth.currentUser;
        if (!user) {
          throw new Error('Không có người dùng nào đăng nhập');
        }
        
        console.log('ID người dùng hiện tại:', user.uid);
        // Gọi cloud function để lấy dữ liệu hồ sơ người dùng
        const getUserProfile = httpsCallable(functions, 'getUserProfile');
        const result = await getUserProfile({ userId });
        const { user: userData, teams, tasks } = result.data;
        
        setUser(userData);
        // Hiển thị tất cả phòng ban mà user là member
        // Bao gồm cả private teams vì user đã là member của chúng
        setUserTeams(teams || []);
        // Hiển thị công việc từ tất cả phòng ban (public hoặc private)
        // vì user là member của những phòng ban đó
        setUserTasks(tasks || []);
        
        // Khởi tạo biểu mẫu chỉnh sửa
        setEditedUser({
          displayName: userData.displayName || '',
          email: userData.email || '',
          bio: userData.bio || '',
          role: userData.role || '',
          phoneNumber: userData.phoneNumber || ''
        });
        
      } catch (err) {
        console.error('Lỗi khi lấy dữ liệu người dùng:', err);
        setError(err.message || 'Không thể tải dữ liệu người dùng');
      } finally {
        setLoading(false);
      }
    };
    
    if (userId) {
      fetchUserData();
    }
  }, [userId, functions]);
  
  // Xử lý thay đổi đầu vào biểu mẫu
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Xử lý thay đổi mật khẩu
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Lưu thay đổi hồ sơ
  const handleSaveProfile = async () => {
    try {
      if (!currentUser || currentUser.uid !== userId) {
        setError('Bạn chỉ có thể chỉnh sửa hồ sơ của chính mình');
        showNotification('Bạn chỉ có thể chỉnh sửa hồ sơ của chính mình', 'error');
        return;
      }
      
      // Cập nhật tên hiển thị trong Firebase Auth
      await updateProfile(currentUser, {
        displayName: editedUser.displayName
      });
      
      // Gọi cloud function để cập nhật hồ sơ người dùng
      const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
      await updateUserProfile({
        userId,
        profileData: {
          ...editedUser,
          email: currentUser.email // Luôn sử dụng email đã xác thực
        }
      });
      
      // Cập nhật trạng thái cục bộ
      setUser(prev => ({
        ...prev,
        ...editedUser,
        email: currentUser.email // Đảm bảo email trong trạng thái cục bộ khớp với email xác thực
      }));
      
      setIsEditing(false);
      showNotification('Cập nhật hồ sơ thành công', 'success');
    } catch (err) {
      console.error('Lỗi khi cập nhật hồ sơ:', err);
      setError('Không thể cập nhật hồ sơ: ' + (err.message || err));
      showNotification('Không thể cập nhật hồ sơ: ' + (err.message || err), 'error');
    }
  };
  
  // Thay đổi mật khẩu
  const handleChangePassword = async () => {
    try {
      setPasswordError('');
      setPasswordSuccess('');
      
      if (!currentUser || currentUser.uid !== userId) {
        setPasswordError('Bạn chỉ có thể thay đổi mật khẩu của chính mình');
        showNotification('Bạn chỉ có thể thay đổi mật khẩu của chính mình', 'error');
        return;
      }
      
      // Xác thực mật khẩu mới
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('Mật khẩu mới không khớp');
        showNotification('Mật khẩu mới không khớp', 'error');
        return;
      }
      
      if (passwordData.newPassword.length < 6) {
        setPasswordError('Mật khẩu phải có ít nhất 6 ký tự');
        showNotification('Mật khẩu phải có ít nhất 6 ký tự', 'error');
        return;
      }
      
      // Xác thực lại người dùng
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(currentUser, credential);
      
      // Cập nhật mật khẩu
      await updatePassword(currentUser, passwordData.newPassword);
      
      // Đặt lại biểu mẫu
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setPasswordSuccess('Cập nhật mật khẩu thành công');
      showNotification('Cập nhật mật khẩu thành công', 'success');
    } catch (err) {
      console.error('Lỗi khi thay đổi mật khẩu:', err);
      
      if (err.code === 'auth/wrong-password') {
        setPasswordError('Mật khẩu hiện tại không đúng');
        showNotification('Mật khẩu hiện tại không đúng', 'error');
      } else {
        setPasswordError('Mật khẩu hiện tại không đúng');
        showNotification('Không thể thay đổi mật khẩu: ' + (err.message || err), 'error');
      }
    }
  };
  
  // Chuyển đổi timestamp thành đối tượng Date
  const timestampToDate = (timestampInput) => {
    if (!timestampInput) {
      return null; // Trả về null cho đầu vào không hợp lệ
    }
  
    // 1. Kiểm tra nếu đã là đối tượng JS Date
    if (timestampInput instanceof Date) {
      return timestampInput;
    }
  
    // 2. Kiểm tra nếu có phương thức .toDate (Firestore Timestamp)
    if (typeof timestampInput.toDate === 'function') {
      return timestampInput.toDate();
    }
  
    // 3. Kiểm tra nếu là đối tượng được tuần tự hóa { _seconds, _nanoseconds }
    if (typeof timestampInput === 'object' && timestampInput !== null &&
        typeof timestampInput._seconds === 'number' && typeof timestampInput._nanoseconds === 'number') {
      // Chuyển đổi giây và nano giây thành mili giây
      return new Date(timestampInput._seconds * 1000 + timestampInput._nanoseconds / 1000000);
    }
  
    // 4. Thử phân tích trực tiếp (xử lý chuỗi ISO, số mili giây)
    const date = new Date(timestampInput);
    if (!isNaN(date.getTime())) { // Kiểm tra xem ngày có hợp lệ không
        return date;
    }
  
    // 5. Nếu không có phương pháp nào hoạt động, trả về null
    console.warn("Không thể chuyển đổi timestamp thành Date:", timestampInput);
    return null;
  }

  // Định dạng đối tượng date thành chuỗi có thể đọc được
  const formatDate = (dateObj) => {
    if (!dateObj) return 'Không có';
    if (!(dateObj instanceof Date)) return 'Ngày không hợp lệ';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  // Tính ngày hết hạn
  const calculateDueDate = (startDate, duration) => {
    const start = timestampToDate(startDate); // Sử dụng hàm trợ giúp ở đây

    if (!start) return null; // Xử lý ngày bắt đầu không hợp lệ

    const dueDate = new Date(start);
    dueDate.setDate(dueDate.getDate() + (parseInt(duration) || 0));
    return dueDate;
  };
  
  // Xử lý thay đổi avatar
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Xác thực loại tệp
    if (!file.type.match('image.*')) {
      showNotification('Vui lòng chọn một tệp hình ảnh', 'error');
      return;
    }
    
    // Xác thực kích thước tệp (giới hạn 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showNotification('Kích thước hình ảnh phải nhỏ hơn 2MB', 'error');
      return;
    }
    
    setAvatarFile(file);
    
    // Tạo bản xem trước
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Tải lên avatar
  const uploadAvatar = async () => {
    if (!avatarFile || !currentUser) return;
    
    try {
      setUploadingAvatar(true);
      
      // Tạo tham chiếu lưu trữ
      const storage = getStorage();
      const avatarRef = ref(storage, `avatars/${currentUser.uid}/${Date.now()}_${avatarFile.name}`);
      
      // Tải lên tệp
      await uploadBytes(avatarRef, avatarFile);
      
      // Lấy URL tải xuống
      const downloadURL = await getDownloadURL(avatarRef);
      
      // Cập nhật hồ sơ người dùng
      await updateProfile(currentUser, {
        photoURL: downloadURL
      });
      
      // Gọi cloud function để cập nhật avatar người dùng
      const updateUserAvatar = httpsCallable(functions, 'updateUserAvatar');
      await updateUserAvatar({
        userId: currentUser.uid,
        photoURL: downloadURL
      });
      
      // Cập nhật trạng thái cục bộ
      setUser(prev => ({
        ...prev,
        photoURL: downloadURL
      }));
      
      // Đặt lại trạng thái tệp
      setAvatarFile(null);
      setAvatarPreview(null);
      
      showNotification('Cập nhật ảnh đại diện thành công');
    } catch (err) {
      console.error('Lỗi khi tải lên avatar:', err);
      showNotification(`Không thể cập nhật ảnh đại diện: ${err.message || err}`, 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };
  
  // Kích hoạt hộp thoại chọn tệp
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg text-center">
            <div className="text-red-600 dark:text-red-400 text-xl mb-4">{error}</div>
            <button
              onClick={() => navigate('/Teams')}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700"
            >
              Quay lại Nhóm
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg text-center">
            <div className="text-yellow-600 dark:text-yellow-400 text-xl mb-4">Không tìm thấy người dùng</div>
            <button
              onClick={() => navigate('/Teams')}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700"
            >
              Quay lại Nhóm
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Header />
      
      {/* Thành phần thông báo */}
      {notification.show && (
        <div className={`fixed top-20 right-4 z-50 p-4 rounded-lg shadow-lg max-w-md transition-all duration-300 transform translate-y-0 
          ${notification.type === 'success' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100' : 
            notification.type === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' : 
            'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'}`}>
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              {notification.type === 'success' ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : notification.type === 'error' ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <div>
              <p className="font-medium">{notification.message}</p>
            </div>
            <button 
              onClick={() => setNotification(prev => ({ ...prev, show: false }))}
              className="ml-auto flex-shrink-0 -mt-1 -mr-1 p-1 rounded-full hover:bg-opacity-20 hover:bg-gray-900 focus:outline-none"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Thẻ hồ sơ cá nhân */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden mb-6 dark:shadow-gray-700/50">
            <div className="p-6 relative">
              {/* Thêm nút chỉnh sửa */}
              {currentUser && currentUser.uid === userId && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="absolute top-4 right-4 bg-emerald-100 dark:bg-emerald-900/30 p-2 rounded-full shadow-md"
                  title="Chỉnh sửa hồ sơ"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
    
              <div className="flex flex-col md:flex-row items-start md:items-center">
                {/* Phần avatar */}
                <div className="mb-4 md:mb-0 md:mr-6">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-emerald-100 dark:border-emerald-900/30">
                      {avatarPreview ? (
                        <img 
                          src={avatarPreview} 
                          alt={user.displayName || 'Người dùng'} 
                          className="w-full h-full object-cover"
                        />
                      ) : user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={user.displayName || 'Người dùng'} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        generateInitialsAvatar({...user}, 'lg')
                      )}
                    </div>
                    
                    {isEditing && (
                      <div 
                        className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        onClick={triggerFileInput}
                      >
                        <span className="text-white text-xs">Đổi ảnh</span>
                      </div>
                    )}
                    
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      className="hidden" 
                      accept="image/*"
                      onChange={handleAvatarChange}
                    />
                  </div>
                  
                  {avatarFile && isEditing && (
                    <div className="mt-2 flex items-center">
                      <button
                        onClick={uploadAvatar}
                        disabled={uploadingAvatar}
                        className="text-xs bg-emerald-500 dark:bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-600 dark:hover:bg-emerald-700 mr-2"
                      >
                        {uploadingAvatar ? 'Đang tải lên...' : 'Lưu ảnh'}
                      </button>
                      <button
                        onClick={() => {
                          setAvatarFile(null);
                          setAvatarPreview(null);
                        }}
                        className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2 py-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        Hủy
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Phần thông tin người dùng */}
                <div className="flex-1">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tên hiển thị</label>
                        <input
                          type="text"
                          name="displayName"
                          value={editedUser.displayName}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                        <input
                          type="email"
                          name="email"
                          value={editedUser.email}
                          readOnly
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Email không thể thay đổi vì lý do bảo mật</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tiểu sử</label>
                        <textarea
                          name="bio"
                          value={editedUser.bio}
                          onChange={handleInputChange}
                          rows="3"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                        ></textarea>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chức vụ</label>
                        <input 
                          type="text"
                          name="role"
                          value={editedUser.role}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                          placeholder="VD: Nhà phát triển, Nhà thiết kế, Quản lý dự án"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Số điện thoại</label>
                        <input
                          type="text"
                          name="phoneNumber"
                          value={editedUser.phoneNumber}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      
                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditedUser({
                              displayName: user.displayName || '',
                              email: currentUser.email || user.email || '', // Luôn sử dụng email đã xác thực
                              bio: user.bio || '',
                              role: user.role || '',
                              phoneNumber: user.phoneNumber || ''
                            });
                          }}
                          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        >
                          Hủy
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          className="px-4 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors"
                        >
                          Lưu thay đổi
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{user?.displayName}</h1>
                      <p className="text-gray-500 dark:text-gray-400 mb-4">{user?.email}</p>
                      
                      {user?.bio && (
                        <div className="mb-4">
                          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tiểu sử</h2>
                          <p className="text-gray-800 dark:text-gray-200">{user.bio}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        {user?.role && (
                          <div>
                            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chức vụ</h2>
                            <p className="text-gray-800 dark:text-gray-200">{user.role}</p>
                          </div>
                        )}
                        
                        {user?.phoneNumber && (
                          <div>
                            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Điện thoại</h2>
                            <p className="text-gray-800 dark:text-gray-200">{user.phoneNumber}</p>
                          </div>
                        )}
                        
                        <div>
                          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Thành viên từ</h2>
                          <p className="text-gray-800 dark:text-gray-200">{formatDate(timestampToDate(user?.createdAt))}</p>
                        </div>
                        
                        <div>
                          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nhóm</h2>
                          <p className="text-gray-800 dark:text-gray-200">{userTeams.length}</p>
                        </div>
                      </div>
                      
                      {currentUser && currentUser.uid === userId && (
                        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => setShowPasswordChange(!showPasswordChange)}
                            className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-500 font-medium"
                          >
                            {showPasswordChange ? 'Hủy thay đổi mật khẩu' : 'Đổi mật khẩu'}
                          </button>
                          
                          {showPasswordChange && (
                            <div className="mt-4 space-y-4">
                              {passwordError && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
                                  {passwordError}
                                </div>
                              )}
                              
                              {passwordSuccess && (
                                <div className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg">
                                  {passwordSuccess}
                                </div>
                              )}
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mật khẩu hiện tại</label>
                                <input
                                  type="password"
                                  name="currentPassword"
                                  value={passwordData.currentPassword}
                                  onChange={handlePasswordChange}
                                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mật khẩu mới</label>
                                <input
                                  type="password"
                                  name="newPassword"
                                  value={passwordData.newPassword}
                                  onChange={handlePasswordChange}
                                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Xác nhận mật khẩu mới</label>
                                <input
                                  type="password"
                                  name="confirmPassword"
                                  value={passwordData.confirmPassword}
                                  onChange={handlePasswordChange}
                                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                                />
                              </div>
                              
                              <button
                                onClick={handleChangePassword}
                                className="px-4 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors"
                              >
                                Cập nhật mật khẩu
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Phòng ban của người dùng */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden mb-6 dark:shadow-gray-700/50">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Phòng ban</h2>
            </div>
            
            <div className="p-6">
              {userTeams.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">Chưa tham gia phòng ban nào.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userTeams.map(team => (
                    <div key={team.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md dark:hover:shadow-gray-700/30 transition-shadow">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{team.name}</h3>
                        {team.private && (
                          <span className="text-gray-500 dark:text-gray-400" title="Phòng ban riêng tư">
                            <i className="fas fa-lock"></i>
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 whitespace-pre-line leading-relaxed">
                        {team.description ? linkifyText(team.description) : null}
                      </p>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {team.membersData?.length || 0} thành viên
                          </span>
                          {team.private && (
                            <span className="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full flex items-center">
                              <i className="fas fa-lock text-xs mr-1"></i>
                              <span className="text-xs">Riêng tư</span>
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => navigate(`/team/${team.id}/tasks`)}
                          className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-500"
                        >
                          Xem phòng ban
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Công việc được giao */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden dark:shadow-gray-700/50">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Công việc được giao</h2>
            </div>
            
            <div className="p-6">
              {userTasks.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">Chưa có công việc nào được giao.</p>
              ) : (
                <div className="space-y-4">
                  {userTasks.map(task => (
                    <div key={task.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md dark:hover:shadow-gray-700/30 transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{task.text}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          task.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' :
                          task.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' :
                          'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400'
                        }`}>
                          {task.priority === 'high' ? 'Ưu tiên cao' : 
                           task.priority === 'medium' ? 'Ưu tiên trung bình' :
                           'Ưu tiên thấp'}
                        </span>
                      </div>
                      {/* Vai trò của bạn trong công việc này */}
                      {currentUser && (() => {
                        const role = getUserRoleInTask(task, currentUser);
                        if (!role) return null;
                        let color = 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
                        let icon = 'fas fa-user-tag';
                        if (role.includes('phê duyệt')) { color = 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'; icon = 'fas fa-user-check'; }
                        else if (role.includes('giám sát')) { color = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'; icon = 'fas fa-user-shield'; }
                        else if (role.includes('phối hợp')) { color = 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200'; icon = 'fas fa-user-friends'; }
                        else if (role.includes('thực hiện')) { color = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'; icon = 'fas fa-user-check'; }
                        return (
                          <div className={`inline-flex items-center px-3 py-1 mb-2 rounded-full text-xs font-semibold ${color}`}
                            title="Vai trò của bạn trong công việc này">
                            <i className={`${icon} mr-2`}></i>
                            Bạn là: {role}
                          </div>
                        );
                      })()}
                      
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 whitespace-pre-line leading-relaxed">
                        {task.description || 'Không có mô tả.'}
                      </p>
                      
                      <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <div>
                          <span className="font-medium">Nhóm:</span> {task.teamName}
                        </div>
                        <div>
                          <span className="font-medium">Trạng thái:</span> {
                            task.status === 'notStarted' ? 'Chưa bắt đầu' :
                            task.status === 'inProgress' || task.status === 'in-progress' ? 'Đang thực hiện' :
                            task.status === 'completed' ? 'Hoàn thành' :
                            task.status === 'pendingApproval' ? 'Chờ phê duyệt' :
                            task.status === 'approved' ? 'Đã phê duyệt' :
                            task.status === 'rejected' ? 'Bị từ chối' :
                            // task.status === 'onHold' ? 'Tạm dừng' :
                            task.status === 'canceled' ? 'Đã hủy' :
                            (task.status ? task.status.charAt(0).toUpperCase() + task.status.slice(1) : 'Chưa bắt đầu')
                          }
                        </div>
                        <div>
                          <span className="font-medium">Hạn chót:</span> {formatDate(timestampToDate(calculateDueDate(task.start_date, task.duration)))}
                        </div>
                        <div>
                          <span className="font-medium">Tiến độ:</span> {task.progress}%
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                        <button
                          onClick={() => navigate(`/team/${task.teamId}/tasks?taskId=${task.id}`)}
                          className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-500"
                        >
                          Xem công việc
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;